import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { getSession, onAuthStateChange, signOut } from "@/lib/supabase/auth";
import { User } from "@/lib/supabase/types";
import { useNavigate } from "react-router-dom";
import { debug } from "@/lib/utils";

type AuthContextType = {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  signOut: async () => { 
    // This is just a placeholder for the default context value
    debug.warn('AuthContext not properly initialized');
  },
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for active session
    const checkSession = async () => {
      try {
        const { data } = await getSession();
        setUser(data.session?.user ?? null);
      } catch (error) {
        debug.error("Error fetching session:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();

    // Listen for auth changes
    const { data: authListener } = onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null);
        setIsLoading(false);
      }
    );

    // Clear user state when a 401 auto-logout fires from the API client
    const handleAuthExpired = () => {
      setUser(null);
    };
    window.addEventListener('tp:auth-expired', handleAuthExpired);

    return () => {
      authListener?.subscription.unsubscribe();
      window.removeEventListener('tp:auth-expired', handleAuthExpired);
    };
  }, []);

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    signOut, // Include the signOut function from supabase/auth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};